import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceSubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [serviceId, setServiceId] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const service_id = searchParams.get('service_id');
    
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    setServiceId(service_id);
    checkPaymentStatus(sessionId);
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attemptCount = 0) => {
    if (attemptCount >= 10) {
      toast.error('Payment verification timed out');
      setStatus('error');
      return;
    }

    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/payments/service-subscription/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Subscription successful! You now have access to all tips from this service.');
        return;
      }

      if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => checkPaymentStatus(sessionId, attemptCount + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      toast.error('Error verifying payment');
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="glass-dark border-white/10 max-w-md w-full">
          <CardHeader>
            <CardTitle className="text-white text-center text-2xl">
              {status === 'loading' && (
                <div className="flex flex-col items-center gap-4">
                  <Loader className="w-12 h-12 text-blue-400 animate-spin" />
                  <h2 className="text-2xl font-bold">Verifying Payment</h2>
                </div>
              )}
              {status === 'success' && (
                <div className="flex flex-col items-center gap-4">
                  <CheckCircle className="w-16 h-16 text-green-400" />
                  <h2 className="text-2xl font-bold">Subscription Active!</h2>
                </div>
              )}
              {(status === 'error' || status === 'expired') && (
                <div className="flex flex-col items-center gap-4">
                  <XCircle className="w-16 h-16 text-red-400" />
                  <h2 className="text-2xl font-bold">Payment {status === 'expired' ? 'Expired' : 'Error'}</h2>
                </div>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            {status === 'loading' && (
              <p className="text-gray-400">Please wait while we confirm your payment...</p>
            )}
            {status === 'success' && (
              <>
                <p className="text-gray-300 mb-6">Your monthly subscription is now active. Enjoy unlimited access to all tips!</p>
                <Button
                  onClick={() => navigate(`/tip-services/${serviceId}`)}
                  className="bg-blue-600 hover:bg-blue-700 w-full"
                >
                  View Service
                </Button>
              </>
            )}
            {(status === 'error' || status === 'expired') && (
              <>
                <p className="text-gray-300 mb-6">
                  {status === 'expired'
                    ? 'Your payment session has expired. Please try again.'
                    : 'There was an error verifying your payment. Please contact support if you were charged.'}
                </p>
                <Button
                  onClick={() => navigate('/tip-services')}
                  className="bg-gray-600 hover:bg-gray-700 w-full"
                >
                  Back to Services
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ServiceSubscriptionSuccess;